import React, { useEffect, useState } from "react";
import { Button, Col, PopoverBody, Row, UncontrolledPopover } from "reactstrap";
import { DASHBOARD_USER_ROLES } from "../../../constants";
import { formatPhoneNumber, getAbreviatedState } from "../../../utils/utils";
import {
  getPaymentInformation1,
  setUpBank,
} from "../../../slices/general/thunk";
import { useStripe } from "@stripe/react-stripe-js";
import Swal from "sweetalert2";
import { useDispatch } from "react-redux";
import logosvgAdmin from "../../../assets/images/svg/clarity_employee-group-line.svg";
import { useNavigate } from "react-router-dom";
import { useCurrentUser } from "../../../slices/auth/hooks";

const Info = ({ currentUser, onEdit, isEditing, tabChange, isUserInvited }) => {
  const stripe = useStripe();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [isCodeCopied, setIsCodeCopied] = React.useState(false);

  const [clientSecret, setClientSecret] = useState("");
  const [loaderStripe, setLoaderStripe] = useState(false);

  const [loading, setLoading] = useState(false);

  const [paymentMethodAdded, setPaymentMethodAdded] = useState(false);

  const handleEditInformation = () => {
    onEdit();
    tabChange("1");
  };

  const handleInviteUser = () => {
    navigate("/invite-users");
  };

  const handleCopyInviteCode = () => {
    setIsCodeCopied(true);
    navigator.clipboard.writeText(currentUser.inviteCode);
    setTimeout(() => {
      setIsCodeCopied(false);
    }, 3000);
  };

  const renderUserRoleAbove = () => {
    // Check one role above only if user has that info.

    // Get info about the user that invited this user
    // i.e the user that either created an account for this user, or sent an invite code to this user.
    const invitedByUserInfo = currentUser?.invitedByUserInfo;

    if (!invitedByUserInfo) return null;

    const { fullName } = invitedByUserInfo;

    let text = "";

    // TODO: To be implemented
    switch (currentUser.role) {
      case DASHBOARD_USER_ROLES.GA:
        text = "National: ";
        break;
      case DASHBOARD_USER_ROLES.BROKER:
        text = "GA: ";
        break;
      case DASHBOARD_USER_ROLES.EMPLOYER:
        text = "Broker: ";
        break;
      default:
        break;
    }

    text = `${text}${fullName}`;

    return <h5>{text}</h5>;
  };

  const renderCurrentUserInviteCode = () => {
    if (currentUser?.role !== DASHBOARD_USER_ROLES.EMPLOYER) return null;
    let text = "";
    if (currentUser?.inviteCode) {
      switch (currentUser.role) {
        case DASHBOARD_USER_ROLES.NATIONAL:
          text = "National";
          break;
        case DASHBOARD_USER_ROLES.GA:
          text = "GA";
          break;
        case DASHBOARD_USER_ROLES.BROKER:
          text = "Broker";
          break;
        case DASHBOARD_USER_ROLES.EMPLOYER:
          text = "Employer";
          break;
        default:
          text = "Invite";
          break;
      }
      // text = ${text} Code: ${currentUser.inviteCode}
      return (
        <div className="">
          <h5 className="ms-1"> {text} Code</h5>
          <div className="border rounded-4 p-2 ">
            <h5 className="d-flex justify-content-between align-items-center mb-0">
              <span className="text-muted fw-normal">
                {" "}
                {currentUser.inviteCode}
              </span>
              <Button
                id="copyInviteCode"
                onClick={handleCopyInviteCode}
                color="soft-primary"
                className="ms-2 py-2
                border border-primary 
                me-3  d-flex align-items-end justify-content-center "
              >
                {isCodeCopied ? (
                  <>
                    <i className="ri-check-line me-2"></i>
                    Copied!
                  </>
                ) : (
                  <>
                    <i className="ri-file-copy-line me-2"></i>
                    <span> Copy Code</span>
                  </>
                )}
              </Button>
              <UncontrolledPopover
                onClick={(e) => e.stopPropagation()}
                placement="bottom"
                target={`copyInviteCode`}
                trigger="hover"
              >
                <PopoverBody className="p-1">
                  {isCodeCopied ? "Copied" : "Copy your invite code"}
                </PopoverBody>
              </UncontrolledPopover>
            </h5>
          </div>
        </div>
      );
    }

    return null;
  };

  const renderEntitiesBelow = () => {
    const entitiesBelow = currentUser.entitiesBelow;
    const entitiesBelowArray = Object.keys(entitiesBelow).map((key) => {
      return {
        name: key,
        count: entitiesBelow[key],
      };
    });

    const getOrder = (name) => {
      switch (name) {
        case "numberOfGAs":
          return 1;
        case "numberOfBrokers":
          return 2;
        case "numberOfEmployers":
          return 3;
        case "numberOfEmployees":
          return 4;
        case "numberOfIndividuals":
          return 5;
        case "numberOfActiveIndividuals":
          return 6;
        default:
          return 99;
      }
    };

    const getDisplayName = (name) => {
      switch (name) {
        case "numberOfGAs":
          return "GAs";
        case "numberOfBrokers":
          return "Brokers";
        case "numberOfEmployers":
          return "Employers";
        case "numberOfEmployees":
          return "Employees";
        case "numberOfIndividuals":
          return "Individuals";
        case "numberOfActiveIndividuals":
          return "Paying Members";
        default:
          return null;
      }
    };

    // Filter out unknown entities and sort by order
    const filteredEntities = entitiesBelowArray
      .filter((entity) => getDisplayName(entity.name) !== null)
      .sort((a, b) => getOrder(a.name) - getOrder(b.name));

    return (
      <div
        className="d-flex justify-content-start align-items-start w-100 flex-wrap"
        style={{ gap: "12px" }}
      >
        {filteredEntities.map((entity, index) => {
          const displayName = getDisplayName(entity.name);

          return (
            <div
              key={index}
              className="border rounded-4 d-flex flex-column align-items-center p-2"
              style={{ minWidth: "100px", flex: "1 1 auto", maxWidth: "150px" }}
            >
              <span className="text-primary fs-4 text-center">{entity.count}</span>
              <span className="text-muted fw-inherit text-center" style={{ fontSize: "0.85rem" }}>{displayName}</span>
            </div>
          );
        })}
      </div>
    );
  };

  const renderEntitiesForAdmin = () => {
    const entitiesBelow = currentUser.entitiesBelow;
    const entitiesBelowArray = Object.keys(entitiesBelow).map((key) => {
      return {
        name: key,
        count: entitiesBelow[key],
      };
    });

    const getOrder = (name) => {
      switch (name) {
        case "numberOfGAs":
          return 1;
        case "numberOfBrokers":
          return 2;
        case "numberOfEmployers":
          return 3;
        case "numberOfEmployees":
          return 4;
        case "numberOfIndividuals":
          return 5;
        case "numberOfActiveIndividuals":
          return 6;
        default:
          return 99;
      }
    };

    const getDisplayName = (name) => {
      switch (name) {
        case "numberOfGAs":
          return "GAs";
        case "numberOfBrokers":
          return "Brokers";
        case "numberOfEmployers":
          return "Employers";
        case "numberOfEmployees":
          return "Employees";
        case "numberOfIndividuals":
          return "Individuals";
        case "numberOfActiveIndividuals":
          return "Paying Members";
        default:
          return null;
      }
    };

    // Filter out unknown entities and sort by order
    const filteredEntities = entitiesBelowArray
      .filter((entity) => getDisplayName(entity.name) !== null)
      .sort((a, b) => getOrder(a.name) - getOrder(b.name));

    return (
      <div
        className="d-flex justify-content-start align-items-stretch w-100 flex-wrap"
        style={{ gap: "12px" }}
      >
        {filteredEntities.map((entity, index) => {
          const displayName = getDisplayName(entity.name);

          return (
            <div
              key={index}
              className="border rounded-4 d-flex flex-column align-items-start p-3 shadow"
              style={{ minWidth: "140px", flex: "1 1 auto", maxWidth: "180px" }}
            >
              <div className="d-flex justify-content-between align-items-center w-100">
                <h6 className="mb-0">{displayName}</h6>
                <div className="bg bg-soft-primary p-1 border rounded">
                  <img src={logosvgAdmin} alt="group" />
                </div>
              </div>
              <span className="text-primary fs-2 text-start">{entity.count}</span>
            </div>
          );
        })}
      </div>
    );
  };

  const renderEntitiesBelowConditional = () => {
    if (DASHBOARD_USER_ROLES.ADMIN === currentUser?.role) {
      return renderEntitiesForAdmin();
    } else {
      // Return non-admin view
      return renderEntitiesBelow();
    }
  };

  return (
    <div className="mb-0 ">
      <Row className="d-flex align-items-center">
        <Col xl={5} lg={12} md={12} sm={12} xs={12}>
          <Col
            xl={7}
            lg={12}
            md={12}
            sm={12}
            xs={12}
            className="border rounded-4 p-3 col-6"
          >
            <section aria-labelledby="user-info-title" className="mb-2">
              <h4 id="user-info-title">{`${currentUser.firstName} ${currentUser.lastName}`}</h4>
              <h6 className="text-muted fw-normal">{currentUser.email}</h6>
              {currentUser.phoneNumber && (
                <h6 className="text-muted fw-normal">
                  {formatPhoneNumber(currentUser.phoneNumber)}
                </h6>
              )}
            </section>
            <hr />

            {(currentUser.companyName || currentUser.companyAddress) && (
              <section aria-labelledby="company-info-title" className="mt-2">
                {currentUser.companyName && <h4>{currentUser.companyName}</h4>}
                {currentUser.companyAddress && (
                  <h6 className="text-muted fw-normal">
                    {currentUser.companyAddress}
                  </h6>
                )}

                <h6>
                  {currentUser.city ? (
                    <span className="text-muted fw-normal">
                      {currentUser.city}
                      {currentUser.state
                        ? `, ${getAbreviatedState(currentUser.state)}`
                        : ""}
                      {currentUser.zipCode ? ` ${currentUser.zipCode}` : ""}
                    </span>
                  ) : (
                    <span className="text-muted fw-normal">
                      {currentUser.state
                        ? `${getAbreviatedState(currentUser.state)}`
                        : "Example"}
                      {currentUser.zipCode ? ` ${currentUser.zipCode}` : ""}
                    </span>
                  )}
                </h6>
              </section>
            )}
          </Col>
        </Col>
        <Col xl={7} lg={12} md={12} className=" mt-3 mt-xl-0">
          <Row className="d-flex align-items-start ">
            <Col lg={8} md={12}>
              {/* <div className="justify-content-end border rounded p-3 d-flex align-items-start flex-column">
                {renderUserRoleAbove()}
              </div> */}
              <div className="mb-4 w-100" style={{ overflow: "hidden" }}>
                {renderEntitiesBelowConditional()}
              </div>

              {currentUser?.inviteCode && renderCurrentUserInviteCode()}
            </Col>
            <Col
              lg={4}
              md={12}
              className="d-flex text-end justify-content-end align-items-end"
            >
              <div className="d-flex flex-lg-column">
                <Button
                  onClick={handleEditInformation}
                  className="btn btn-primary mt-xl-0 mt-3 "
                >
                  {!isEditing ? "Edit Information" : "Cancel Edit"}
                </Button>
                {!isUserInvited && (
                  <Button
                    onClick={handleInviteUser}
                    className="btn btn-primary mt-3 mt-3 ms-lg-0 ms-2 "
                  >
                    Manage Users
                  </Button>
                )}
              </div>
            </Col>
          </Row>
        </Col>
      </Row>
    </div>
  );
};

export default Info;
